import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import type { Me } from "@atrium/shared";
import { ApiError, api } from "../../lib/api";
import { connectSocket, disconnectSocket } from "../../lib/socket";
import { endSession, initSession } from "../../lib/e2ee/session";

interface AuthCtx {
  me: Me | null;
  loading: boolean;
  setMe: (me: Me | null) => void;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<Me>("/api/auth/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: Infinity,
    retry: 1,
  });
  const me = data ?? null;

  // Connect the socket synchronously once authenticated so children can subscribe on mount.
  useMemo(() => {
    if (me) connectSocket();
  }, [me?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Encrypted-messages session. Re-checked whenever `me` refetches (a key reset on another device shows up as a me:update).
  useEffect(() => {
    if (me) void initSession(me.id).catch(() => {});
  }, [me]);

  useEffect(() => {
    if (!me) disconnectSocket();
  }, [me]);

  useEffect(() => {
    const onUnauthorized = () => {
      if (qc.getQueryData(["me"])) qc.setQueryData(["me"], null);
    };
    window.addEventListener("atrium:unauthorized", onUnauthorized);
    return () => window.removeEventListener("atrium:unauthorized", onUnauthorized);
  }, [qc]);

  const setMe = useCallback((m: Me | null) => qc.setQueryData(["me"], m), [qc]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      // Sign out locally even if the request fails (offline, expired session).
    } finally {
      disconnectSocket();
      // This device's private keys go with the session; the recovery code brings them back.
      await endSession();
      // Mark signed out first, while the auth query's observers are still attached:
      // clearing the whole cache first detaches them and the app never notices.
      await qc.cancelQueries();
      qc.setQueryData(["me"], null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    }
  }, [qc]);

  return <Ctx.Provider value={{ me, loading: isLoading, setMe, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

/** The main app's auth state, or null where there's no AuthProvider (the developer portal and docs sites). */
export function useOptionalAuth() {
  return useContext(Ctx);
}

export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error("useMe requires an authenticated user");
  return me;
}

/**
 * Wrap actions that need an account. Signed-out visitors (on public pages)
 * are sent to sign in and returned afterwards.
 */
export function useAuthGate() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (!me) {
          navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
          return;
        }
        fn(...args);
      },
    [me, navigate, location],
  );
}
